import {
  ClaimFormsApi,
  ClaimFormResponse,
  ClaimFormGeoDataResponse,
  GetClaimFormByIdRequest,
  UpdateClaimFormByIdRequest,
  GetClaimFormSchemaRequest,
  GetClaimFormGeoDataRequest,
  SearchClaimFormsRequest,
  PageClaimFormSearchResult,
} from '@Generated/back-office-client/src'
import { ResponseError } from '@Generated/back-office-client/src/runtime'
import { BackOfficeDataServiceAPI } from './back-office-data-service'
import { ClaimFormPdfResult, ClaimFormPdfStatus } from '@Components/claims/claim-form-pdf-types'

const searchClaimForms = async (
  query: string,
  resultStart: number,
  resultEnd: number,
  startDate?: string,
  endDate?: string,
  linkStatus?: string,
): Promise<PageClaimFormSearchResult> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  const request: SearchClaimFormsRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    query,
    resultStart,
    resultEnd,
    startDate,
    endDate,
    linkStatus,
  }

  return await api.searchClaimForms(request)
}

const linkClaimForm = async (
  claimFormId: string,
  claimReservationId: string,
): Promise<ClaimFormResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  return await api.linkClaimFormToReservation({
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimFormId,
    claimReservationId,
  })
}

const relinkClaimForm = async (
  claimFormId: string,
  claimReservationId: string,
): Promise<ClaimFormResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  return await api.relinkClaimFormToReservation({
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimFormId,
    claimReservationId,
  })
}

const unlinkClaimForm = async (claimFormId: string, claimReservationId: string): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  await api.unlinkClaimFormFromReservation({
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimFormId,
    claimReservationId,
  })
}

const getClaimFormById = async (claimFormId: string): Promise<ClaimFormResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  const request: GetClaimFormByIdRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimFormId,
  }

  return await api.getClaimFormById(request)
}

const markClaimFormReceived = async (claimFormId: string): Promise<ClaimFormResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  return await api.markClaimFormReceived({
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimFormId,
  })
}

const markClaimFormAuthorized = async (claimFormId: string): Promise<ClaimFormResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  return await api.markClaimFormAuthorized({
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimFormId,
  })
}

const updateClaimFormById = async (
  claimFormId: string,
  formData: Record<string, unknown>,
): Promise<ClaimFormResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  const request: UpdateClaimFormByIdRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimFormId,
    requestBody: formData,
  }

  return await api.updateClaimFormById(request)
}

const getClaimFormSchema = async (): Promise<Record<string, unknown>> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  const request: GetClaimFormSchemaRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }

  return await api.getClaimFormSchema(request)
}

const getClaimFormGeoData = async (): Promise<ClaimFormGeoDataResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)
  const request: GetClaimFormGeoDataRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }

  return await api.getClaimFormGeoData(request)
}

/**
 * Downloads a claim form's PDF.
 *
 * Uses the generated client's `*Raw` method because the typed method deserializes the body to a
 * string; `*Raw` leaves the response untouched so the bytes can be read as a blob, while still
 * reusing the client's base path and auth.
 */
const downloadClaimFormPdf = async (claimFormId: string): Promise<ClaimFormPdfResult> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)

  try {
    const response = await api.downloadClaimFormPdfRaw({
      customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
      claimFormId,
    })

    return { status: ClaimFormPdfStatus.READY, blob: await response.raw.blob() }
  } catch (e) {
    if (e instanceof ResponseError && e.response.status === 409) {
      return { status: ClaimFormPdfStatus.GENERATING }
    }
    throw e
  }
}

/**
 * Downloads the zip archive of claim forms a claim center received on a date. Resolves to null when
 * no archive exists, which means that center received nothing that day or the nightly job has not
 * run yet.
 */
const downloadClaimFormArchive = async (
  claimCenterId: string,
  date: string,
): Promise<Blob | null> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimFormsApi)

  try {
    const response = await api.downloadClaimFormArchiveRaw({
      customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
      claimCenterId,
      date,
    })

    return await response.raw.blob()
  } catch (e) {
    if (e instanceof ResponseError && e.response.status === 404) {
      return null
    }
    throw e
  }
}

export const ClaimFormsServiceAPI = {
  searchClaimForms,
  linkClaimForm,
  relinkClaimForm,
  unlinkClaimForm,
  getClaimFormById,
  markClaimFormReceived,
  markClaimFormAuthorized,
  updateClaimFormById,
  getClaimFormSchema,
  getClaimFormGeoData,
  downloadClaimFormPdf,
  downloadClaimFormArchive,
}
